import React, { useState, useEffect } from 'react';
import { EmotionMood } from '../../types';
import { Scissors, Mic, Layers, User } from 'lucide-react';
import { UserSessionModal } from './UserSessionModal';
import { CharacterAvatar } from './CharacterAvatar';
import { Logo } from './Logo';
import { TokenBadge } from '../tokens/TokenBadge';
import { TokenPurchaseModal } from '../tokens/TokenPurchaseModal';
import { fetchUserProfile } from '../../services/vaultService';

interface HeaderProps {
  currentTab: 'capture' | 'sync' | 'share';
  onTabChange: (tab: 'capture' | 'sync' | 'share') => void;
  userName?: string;
  onUserLogin?: (name: string) => void;
  currentMood?: EmotionMood;
  onMoodChange?: (mood: EmotionMood) => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentTab,
  onTabChange,
  userName = '',
  onUserLogin
}) => {
  const [isUserModalOpen, setIsUserModalOpen] = useState<boolean>(false);
  const [userAvatarUrl, setUserAvatarUrl] = useState<string>('char-maverick');

  useEffect(() => {
    if (userName) {
      fetchUserProfile(userName).then((p) => {
        if (p?.avatarUrl) setUserAvatarUrl(p.avatarUrl);
      });
    }
  }, [userName]);

  return (
    <>
      <header className="header-bar">
        <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Brand Identity */}
          <Logo onClick={() => onTabChange('capture')} />

          {/* Workspace Navigation Tabs: SoulCapture -> SoulSync -> SoulShare */}
          <nav className="nav-tabs">
            <button
              className={`nav-tab ${currentTab === 'capture' ? 'active' : ''}`}
              onClick={() => onTabChange('capture')}
              title="Stage 1: Precision scene capture and range trimming"
            >
              <Scissors size={16} />
              <span>SoulCapture</span>
            </button>
            <button
              className={`nav-tab ${currentTab === 'sync' ? 'active' : ''}`}
              onClick={() => onTabChange('sync')}
              title="Stage 2: Stem engine, dialogue fine-tuning & vocal recording"
            >
              <Mic size={16} />
              <span>SoulSync</span>
            </button>
            <button
              className={`nav-tab ${currentTab === 'share' ? 'active' : ''}`}
              onClick={() => onTabChange('share')}
              title="Stage 3: Collaborative recordings, forks & shared takes"
            >
              <Layers size={16} />
              <span>SoulShare</span>
            </button>
          </nav>

          {/* Right Controls: Token Store Badge & User Profile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            {/* Token Store & Balance Badge */}
            <TokenBadge />

            {/* User Session Profile Button */}
            <button
              type="button"
              onClick={() => setIsUserModalOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: userName ? '4px 12px 4px 6px' : '6px 14px',
                borderRadius: 'var(--radius-full, 9999px)',
                background: userName
                  ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.22) 0%, rgba(99, 102, 241, 0.18) 100%)'
                  : 'rgba(255, 255, 255, 0.08)',
                border: userName ? '1px solid rgba(168, 85, 247, 0.55)' : '1px solid rgba(255, 255, 255, 0.15)',
                color: '#ffffff',
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: userName ? '0 0 14px rgba(168, 85, 247, 0.3)' : 'none',
                transition: 'all 0.15s ease'
              }}
              title={userName ? `Logged in as ${userName}. Click to manage session.` : 'Click to sign in and personalize your session'}
            >
              {userName ? (
                <>
                  <CharacterAvatar
                    avatarId={userAvatarUrl}
                    size="xs"
                    fallbackInitial={userName.charAt(0)}
                  />
                  <span style={{ color: '#f3e8ff', letterSpacing: '0.01em' }}>{userName}</span>
                </>
              ) : (
                <>
                  <User size={15} color="#c084fc" />
                  <span>Sign In / Register</span>
                  <span
                    style={{
                      fontSize: '0.68rem',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      background: 'rgba(234, 179, 8, 0.15)',
                      border: '1px solid rgba(234, 179, 8, 0.35)',
                      color: '#facc15',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                      marginLeft: '4px'
                    }}
                    title="Temporary session. Captures stay in browser until signed in."
                  >
                    Temp Vault
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* User Session Modal */}
      <UserSessionModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        currentUserName={userName || ''}
        onSaveUser={(name) => {
          if (onUserLogin) onUserLogin(name);
        }}
        onAvatarChange={(avatarUrl) => {
          setUserAvatarUrl(avatarUrl);
        }}
      />

      {/* Token Store & Purchase Modal */}
      <TokenPurchaseModal />
    </>
  );
};
